import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  XMarkIcon,
  UsersIcon,
  ChatBubbleLeftRightIcon,
  CreditCardIcon,
  ClockIcon,
  MapPinIcon,
  PlusIcon,
  PaperAirplaneIcon,
  ShoppingCartIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { groupOrdersApi } from '../utils/advancedOrdersApi';
import { showSuccessNotification, showErrorNotification } from './NotificationManager';
import GroupOrderMealSelector from './GroupOrderMealSelector';
import ShareGroupOrder from './ShareGroupOrder';

const GroupOrderDetails = ({ orderId, isOpen, onClose, onUpdate }) => {
  const [orderDetails, setOrderDetails] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [showMealSelector, setShowMealSelector] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (isOpen && orderId) {
      fetchOrderDetails();
      fetchChatMessages();
      fetchPaymentStatus();

      // Get current user info
      const userData = localStorage.getItem('byteUser');
      if (userData) {
        try {
          const parsedUser = JSON.parse(userData);
          setCurrentUser(parsedUser);
        } catch (error) {
          console.error('Error parsing user data:', error);
          showErrorNotification('Failed to load user data');
        }
      }
    }
  }, [isOpen, orderId]);

  const fetchOrderDetails = async () => {
    try {
      const response = await groupOrdersApi.getGroupOrderDetails(orderId);
      if (response.success) {
        setOrderDetails(response.data);
      } else {
        showErrorNotification('Failed to fetch order details');
      }
    } catch (error) {
      console.error('Failed to fetch order details:', error);
      showErrorNotification('Failed to fetch order details');
    } finally {
      setLoading(false);
    }
  };

  const fetchChatMessages = async () => {
    try {
      const response = await groupOrdersApi.getChatMessages(orderId);
      if (response.success) {
        // Sort messages by timestamp to ensure latest messages are at the bottom
        const sortedMessages = (response.data || []).sort((a, b) => {
          const timeA = new Date(a.timestamp || a.createdAt || 0);
          const timeB = new Date(b.timestamp || b.createdAt || 0);
          return timeA - timeB;
        });
        setChatMessages(sortedMessages);
        // Auto-scroll to bottom after messages are loaded
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      } else {
        showErrorNotification('Failed to fetch chat messages');
      }
    } catch (error) {
      console.error('Failed to fetch chat messages:', error);
      showErrorNotification('Failed to fetch chat messages');
    }
  };

  const fetchPaymentStatus = async () => {
    try {
      const response = await groupOrdersApi.getPaymentStatus(orderId);
      if (response.success) {
        setPaymentStatus(response.data);
      } else {
        showErrorNotification('Failed to fetch payment status');
      }
    } catch (error) {
      console.error('Failed to fetch payment status:', error);
      showErrorNotification('Failed to fetch payment status');
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) {
      showErrorNotification('Message cannot be empty');
      return;
    }

    setSendingMessage(true);
    try {
      const response = await groupOrdersApi.sendChatMessage(orderId, newMessage.trim());
      if (response.success) {
        setNewMessage('');
        fetchChatMessages(); // Refresh messages
        showSuccessNotification('Message sent successfully');
        // Auto-scroll to bottom after sending message
        setTimeout(() => {
          messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }, 200);
      } else {
        showErrorNotification(response.message || 'Failed to send message');
      }
    } catch (error) {
      showErrorNotification('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const payForOrder = async () => {
    const participant = getCurrentParticipant();

    if (!participant || !participant.meals || participant.meals.length === 0) {
      showErrorNotification('Please add meals to your order before making payment');
      return;
    }

    if (participant.subtotal <= 0) {
      showErrorNotification('Cannot process payment for an empty order');
      return;
    }

    setPaymentLoading(true);
    try {
      const response = await groupOrdersApi.payForGroupOrder(orderId);
      if (response.success) {
        showSuccessNotification('Payment processed successfully!');
        fetchOrderDetails();
        fetchPaymentStatus();
        onUpdate();
      } else {
        showErrorNotification(response.message || 'Payment failed');
      }
    } catch (error) {
      showErrorNotification('Payment failed');
    } finally {
      setPaymentLoading(false);
    }
  };

  const leaveGroupOrder = async () => {
    if (!window.confirm('Are you sure you want to leave this group order? You will be refunded if you have already paid.')) {
      return;
    }

    try {
      const response = await groupOrdersApi.leaveGroupOrder(orderId);
      if (response.success) {
        showSuccessNotification('Successfully left the group order');
        onClose();
        onUpdate();
      } else {
        showErrorNotification(response.message || 'Failed to leave group order');
      }
    } catch (error) {
      showErrorNotification('Failed to leave group order');
    }
  };

  const cancelGroupOrder = async () => {
    if (!window.confirm('Are you sure you want to cancel this group order? All participants will be refunded.')) {
      return;
    }

    try {
      const response = await groupOrdersApi.cancelGroupOrder(orderId);
      if (response.success) {
        showSuccessNotification('Group order cancelled successfully');
        fetchOrderDetails();
        onUpdate();
      } else {
        showErrorNotification(response.message || 'Failed to cancel group order');
      }
    } catch (error) {
      showErrorNotification('Failed to cancel group order');
    }
  };

  const getCurrentParticipant = () => {
    if (!orderDetails?.participants || !currentUser) return null;
    return orderDetails.participants.find(
      (p) => p.user?._id === currentUser?._id || p.user?.username === currentUser?.username
    );
  };

  const isCreator = () => {
    return currentUser && orderDetails?.creator?._id === currentUser?._id;
  };

  const canAddMeals = () => {
    return orderDetails?.status === 'open' && !!getCurrentParticipant();
  };

  const canPay = () => {
    const participant = getCurrentParticipant();
    return (
      orderDetails?.status === 'open' &&
      participant &&
      !participant.hasPaid &&
      participant.subtotal > 0 &&
      participant.meals &&
      participant.meals.length > 0
    );
  };

  const getTimeLeft = (deadline) => {
    if (!deadline) return 'N/A';
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const diff = deadlineDate - now;

    if (diff <= 0) return 'Expired';

    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 24) {
      const days = Math.floor(hours / 24);
      return `${days}d ${hours % 24}h left`;
    }

    return `${hours}h ${minutes}m left`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'closed':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'ordered':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'delivered':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (!isOpen) return null;

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="bg-accentwhite rounded-2xl p-8"
        >
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pepperoni mx-auto"></div>
          <p className="text-olive-light font-sans mt-4">Loading group order...</p>
        </motion.div>
      </div>
    );
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 md:flex md:items-center md:justify-center md:p-4">
          <motion.div
            initial={{ y: '100%', opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: '100%', opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="bg-accentwhite h-full w-full md:rounded-2xl md:shadow-2xl md:max-w-4xl md:w-full md:max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Mobile Header */}
            <div className="flex-shrink-0 bg-accentwhite border-b border-secondary-200">
              {/* Top bar with close button */}
              <div className="flex items-center justify-between p-4 md:hidden">
                <div className="w-8"></div> {/* Spacer */}
                <div className="w-12 h-1 bg-secondary-300 rounded-full"></div> {/* Handle */}
                <button
                  onClick={onClose}
                  className="w-8 h-8 flex items-center justify-center text-olive-light hover:text-pepperoni rounded-full hover:bg-secondary-50 transition-all duration-200"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Order info */}
              <div className="px-4 pb-4 md:p-6">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <UsersIcon className="w-5 h-5 text-pepperoni flex-shrink-0" />
                      <h2 className="text-lg md:text-xl font-bold text-crust font-display truncate">
                        {orderDetails?.title || 'Group Order'}
                      </h2>
                    </div>
                    <p className="text-sm text-olive-light font-sans">
                      {orderDetails?.restaurant?.name || 'Unknown Restaurant'}
                    </p>
                    <p className="text-xs text-olive-light font-sans">
                      by @{orderDetails?.creator?.username || 'Unknown'}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-2 ml-3">
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-bold font-display border ${orderDetails?.status ? getStatusColor(orderDetails.status) : 'bg-gray-100 text-gray-800 border-gray-200'
                        }`}
                    >
                      {orderDetails?.status?.toUpperCase() || 'UNKNOWN'}
                    </span>
                    {/* Desktop close button */}
                    <button
                      onClick={onClose}
                      className="hidden md:flex w-8 h-8 items-center justify-center text-olive-light hover:text-pepperoni rounded-full hover:bg-secondary-50 transition-all duration-200"
                    >
                      <XMarkIcon className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="flex-shrink-0 bg-accentwhite border-b border-secondary-200">
              <div className="flex px-4 md:px-0">
                <button
                  onClick={() => setActiveTab('details')}
                  className={`flex-1 py-3 px-3 md:px-6 font-bold font-display text-sm md:text-base transition-all duration-300 ${activeTab === 'details'
                      ? 'bg-gradient-to-r from-cheese to-secondary-500 text-crust border-b-2 border-pepperoni'
                      : 'text-olive-light hover:text-crust hover:bg-secondary-50'
                    }`}
                >
                  Details
                </button>
                <button
                  onClick={() => setActiveTab('chat')}
                  className={`flex-1 py-3 px-3 md:px-6 font-bold font-display text-sm md:text-base transition-all duration-300 ${activeTab === 'chat'
                      ? 'bg-gradient-to-r from-cheese to-secondary-500 text-crust border-b-2 border-pepperoni'
                      : 'text-olive-light hover:text-crust hover:bg-secondary-50'
                    }`}
                >
                  <div className="flex items-center justify-center gap-1 md:gap-2">
                    <ChatBubbleLeftRightIcon className="w-4 h-4" />
                    <span className="hidden sm:inline">Chat</span> ({chatMessages.length})
                  </div>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              <div className="p-4 md:p-6 pb-20 md:pb-6">
                {activeTab === 'details' && (
                  <div className="space-y-6">
                    {/* Order Info */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-secondary-50 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <ClockIcon className="w-4 h-4 text-pepperoni" />
                          <span className="text-xs text-olive-light font-sans">Order Deadline</span>
                        </div>
                        <div className="font-bold text-crust font-display">
                          {getTimeLeft(orderDetails?.orderDeadline)}
                        </div>
                        <div className="text-xs text-olive-light font-sans">
                          {orderDetails?.orderDeadline
                            ? new Date(orderDetails.orderDeadline).toLocaleString()
                            : 'N/A'}
                        </div>
                      </div>

                      <div className="bg-secondary-50 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <UsersIcon className="w-4 h-4 text-pepperoni" />
                          <span className="text-xs text-olive-light font-sans">Participants</span>
                        </div>
                        <div className="font-bold text-crust font-display">
                          {orderDetails?.participants?.length || 0}/{orderDetails?.maxParticipants || 'N/A'}
                        </div>
                      </div>

                      <div className="bg-secondary-50 rounded-xl p-4">
                        <div className="flex items-center gap-2 mb-2">
                          <CreditCardIcon className="w-4 h-4 text-pepperoni" />
                          <span className="text-xs text-olive-light font-sans">Total Amount</span>
                        </div>
                        <div className="font-bold text-crust font-display">
                          ₦{orderDetails?.totalAmount || 0}
                        </div>
                      </div>
                    </div>

                    {/* Description */}
                    {orderDetails?.description && (
                      <div className="bg-gray-50 rounded-xl p-4">
                        <h4 className="font-bold text-crust font-display mb-2">Description</h4>
                        <p className="text-olive-light font-sans">{orderDetails.description}</p>
                      </div>
                    )}

                    {/* Delivery Info */}
                    {orderDetails?.deliveryInfo && (
                      <div className="bg-gray-50 rounded-xl p-4">
                        <h4 className="font-bold text-crust font-display mb-3 flex items-center gap-2">
                          <MapPinIcon className="w-4 h-4 text-pepperoni" />
                          Delivery Information
                        </h4>
                        <div className="space-y-2 text-sm font-sans">
                          <div>
                            <span className="text-olive-light">Location: </span>
                            <span className="font-bold text-crust">
                              {orderDetails?.deliveryInfo?.location || 'N/A'}
                            </span>
                          </div>
                          <div>
                            <span className="text-olive-light">Phone: </span>
                            <span className="font-bold text-crust">
                              {orderDetails?.deliveryInfo?.phoneNumber || 'N/A'}
                            </span>
                          </div>
                          {orderDetails?.deliveryInfo?.landmark && (
                            <div>
                              <span className="text-olive-light">Landmark: </span>
                              <span className="font-bold text-crust">{orderDetails.deliveryInfo.landmark}</span>
                            </div>
                          )}
                          {orderDetails?.deliveryTime && (
                            <div>
                              <span className="text-olive-light">Delivery Time: </span>
                              <span className="font-bold text-crust">
                                {new Date(orderDetails.deliveryTime).toLocaleString()}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Participants */}
                    {orderDetails?.participants && (
                      <div>
                        <h4 className="font-bold text-crust font-display mb-3">Participants</h4>
                        <div className="space-y-3">
                          {orderDetails.participants.map((participant, index) => {
                            const isCurrentUser =
                              currentUser &&
                              (participant.user?._id === currentUser._id ||
                                participant.user?.username === currentUser.username);

                            return (
                              <div
                                key={participant.user?._id || index}
                                className={`flex items-center justify-between rounded-xl p-4 ${isCurrentUser ? 'bg-primary-50 border-2 border-pepperoni' : 'bg-gray-50'
                                  }`}
                              >
                                <div className="flex items-center gap-3">
                                  <div
                                    className={`w-10 h-10 rounded-full flex items-center justify-center ${isCurrentUser ? 'bg-pepperoni' : 'bg-secondary-400'
                                      }`}
                                  >
                                    <span className="text-accentwhite font-bold text-sm">
                                      {participant.user?.username?.charAt(0).toUpperCase() || '?'}
                                    </span>
                                  </div>
                                  <div>
                                    <div className="font-bold text-crust font-display flex items-center gap-2">
                                      @{participant.user?.username || 'Unknown'}
                                      {isCurrentUser && (
                                        <span className="text-xs bg-pepperoni text-white px-2 py-1 rounded-full">
                                          You
                                        </span>
                                      )}
                                      {participant.user?._id === orderDetails?.creator?._id && (
                                        <span className="text-xs bg-cheese text-crust px-2 py-1 rounded-full">
                                          Creator
                                        </span>
                                      )}
                                    </div>
                                    <div className="text-xs text-olive-light font-sans">
                                      {participant.meals?.length || 0} items
                                    </div>
                                    {participant.meals && participant.meals.length > 0 && (
                                      <div className="text-xs text-olive-light font-sans mt-1">
                                        {participant.meals.map((meal) => meal.meal?.name).join(', ')}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="text-right">
                                  <div className="font-bold text-crust font-display">
                                    ₦{participant.subtotal || 0}
                                  </div>
                                  <div
                                    className={`text-xs font-bold ${participant.hasPaid ? 'text-green-600' : 'text-red-600'
                                      }`}
                                  >
                                    {participant.hasPaid ? 'Paid' : 'Pending'}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Current User Actions */}
                    {canAddMeals() && (
                      <div className="bg-gradient-to-r from-pepperoni/10 to-primary-800/10 rounded-xl p-4 border-2 border-pepperoni/20">
                        <h4 className="font-bold text-crust font-display mb-3 flex items-center gap-2">
                          <ShoppingCartIcon className="w-5 h-5 text-pepperoni" />
                          Your Order
                        </h4>

                        {(!getCurrentParticipant()?.meals || getCurrentParticipant()?.meals?.length === 0) ? (
                          <div className="text-center py-6">
                            <div className="w-12 h-12 md:w-16 md:h-16 bg-secondary-100 rounded-full flex items-center justify-center mx-auto mb-3">
                              <ShoppingCartIcon className="w-6 h-6 md:w-8 md:h-8 text-secondary-400" />
                            </div>
                            <p className="text-olive-light font-sans mb-2 text-sm md:text-base">
                              You haven't selected any meals yet
                            </p>
                            <p className="text-xs md:text-sm text-secondary-500 font-sans mb-4">
                              Add meals from {orderDetails?.restaurant?.name || 'the restaurant'} to join this group order
                            </p>
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => setShowMealSelector(true)}
                              className="bg-pepperoni text-accentwhite px-4 py-2 md:px-6 md:py-3 rounded-xl font-bold font-display shadow-lg hover:shadow-xl transition-all duration-300 flex items-center gap-2 mx-auto text-sm md:text-base"
                            >
                              <PlusIcon className="w-4 h-4 md:w-5 md:h-5" />
                              Select Meals
                            </motion.button>
                          </div>
                        ) : (
                          <div className="space-y-3 md:space-y-0 md:flex md:items-center md:justify-between">
                            <div>
                              <p className="text-sm text-olive-light font-sans">
                                {getCurrentParticipant()?.meals?.length || 0} items selected
                              </p>
                              <p className="text-lg font-bold text-pepperoni font-display">
                                Total: ₦{getCurrentParticipant()?.subtotal || 0}
                              </p>
                            </div>
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => setShowMealSelector(true)}
                              className="w-full md:w-auto bg-pepperoni text-accentwhite px-4 py-2 rounded-xl font-bold font-display shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 text-sm md:text-base"
                            >
                              <PlusIcon className="w-4 h-4" />
                              Update Meals
                            </motion.button>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Payment Status */}
                    {paymentStatus && (
                      <div className="bg-gray-50 rounded-xl p-4">
                        <h4 className="font-bold text-crust font-display mb-3">Payment Status</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                          <div className="text-center">
                            <div className="text-2xl font-bold text-crust font-display">
                              {paymentStatus.groupSummary?.totalParticipants || 0}
                            </div>
                            <div className="text-xs text-olive-light font-sans">Total Participants</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-green-600 font-display">
                              {paymentStatus.groupSummary?.paidParticipants || 0}
                            </div>
                            <div className="text-xs text-olive-light font-sans">Paid</div>
                          </div>
                          <div className="text-center">
                            <div className="text-2xl font-bold text-red-600 font-display">
                              {(paymentStatus.groupSummary?.totalParticipants || 0) -
                                (paymentStatus.groupSummary?.paidParticipants || 0)}
                            </div>
                            <div className="text-xs text-olive-light font-sans">Pending</div>
                          </div>
                        </div>

                        {paymentStatus.groupSummary?.unpaidParticipants?.length > 0 && (
                          <div className="bg-yellow-50 rounded-lg p-3 border border-yellow-200">
                            <div className="flex items-center gap-2 mb-2">
                              <ExclamationTriangleIcon className="w-4 h-4 text-yellow-600" />
                              <span className="text-sm font-bold text-yellow-800">Waiting for Payment</span>
                            </div>
                            <div className="text-xs text-yellow-700">
                              {paymentStatus.groupSummary.unpaidParticipants
                                .map((p) => `@${p.username} (₦${p.amount})`)
                                .join(', ')}
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {/* Share Group Order */}
                    {orderDetails?.shareUrl && orderDetails?.status === 'open' && (
                      <ShareGroupOrder
                        shareUrl={orderDetails.shareUrl}
                        title={orderDetails.title}
                        orderId={orderDetails._id}
                      />
                    )}
                  </div>
                )}

                {activeTab === 'chat' && (
                  <div className="flex flex-col h-full">
                    {/* Messages */}
                    <div className="flex-1 space-y-3 overflow-y-auto mb-4 max-h-[50vh]">
                      {chatMessages.length === 0 ? (
                        <div className="text-center py-8">
                          <ChatBubbleLeftRightIcon className="w-10 h-10 md:w-12 md:h-12 text-secondary-300 mx-auto mb-3" />
                          <p className="text-olive-light font-sans text-sm md:text-base">
                            No messages yet. Start the conversation!
                          </p>
                        </div>
                      ) : (
                        <>
                          {chatMessages.map((message) => (
                            <div key={message._id || message.timestamp} className="flex items-start gap-3">
                              <div className="w-7 h-7 md:w-8 md:h-8 bg-pepperoni rounded-full flex items-center justify-center flex-shrink-0">
                                <span className="text-accentwhite font-bold text-xs">
                                  {message.user?.username?.charAt(0).toUpperCase() || '?'}
                                </span>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  <span className="font-bold text-crust font-display text-sm truncate">
                                    @{message.user?.username || 'Unknown'}
                                  </span>
                                  <span className="text-xs text-olive-light font-sans flex-shrink-0">
                                    {message.timestamp
                                      ? new Date(message.timestamp).toLocaleTimeString()
                                      : 'N/A'}
                                  </span>
                                </div>
                                <p className="text-olive-light font-sans text-sm break-words">
                                  {message.message}
                                </p>
                              </div>
                            </div>
                          ))}
                          {/* Invisible element to scroll to */}
                          <div ref={messagesEndRef} />
                        </>
                      )}
                    </div>

                    {/* Message Input - Fixed at bottom */}
                    <div className="border-t border-secondary-200 pt-4 mt-auto">
                      <form onSubmit={sendMessage} className="flex gap-2 md:gap-3">
                        <input
                          type="text"
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          placeholder="Type a message..."
                          disabled={sendingMessage}
                          className="flex-1 p-3 border border-secondary-300 rounded-xl focus:ring-2 focus:ring-cheese focus:border-transparent font-sans text-sm md:text-base"
                        />
                        <button
                          type="submit"
                          disabled={sendingMessage || !newMessage.trim()}
                          className="bg-pepperoni text-accentwhite p-3 rounded-xl hover:bg-primary-800 transition-all duration-300 disabled:opacity-50 flex-shrink-0"
                        >
                          {sendingMessage ? (
                            <div className="animate-spin rounded-full h-4 w-4 md:h-5 md:w-5 border-b-2 border-accentwhite"></div>
                          ) : (
                            <PaperAirplaneIcon className="w-4 h-4 md:w-5 md:h-5" />
                          )}
                        </button>
                      </form>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Actions - Fixed at bottom on mobile */}
            <div className="flex-shrink-0 bg-accentwhite border-t border-secondary-200 p-4 md:p-6">
              <div className="flex gap-3">
                <div className="flex flex-col md:flex-row gap-3 w-full">
                  {/* Primary Action Button */}
                  {canPay() && (
                    <button
                      onClick={payForOrder}
                      disabled={paymentLoading}
                      className={`w-full md:flex-1 py-3 px-4 rounded-xl font-bold font-display shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 text-sm md:text-base ${
                        paymentLoading
                          ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                          : 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-600 text-white'
                      }`}
                    >
                      {paymentLoading ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 md:h-5 md:w-5 border-2 border-gray-600 border-t-transparent"></div>
                          Processing...
                        </>
                      ) : (
                        <>
                          <CreditCardIcon className="w-4 h-4 md:w-5 md:h-5" />
                          Pay Now (₦{getCurrentParticipant()?.subtotal || 0})
                        </>
                      )}
                    </button>
                  )}

                  {canAddMeals() && !canPay() && (
                    <button
                      onClick={() => setShowMealSelector(true)}
                      className="w-full md:flex-1 py-3 px-4 bg-gradient-to-r from-pepperoni to-primary-800 hover:from-primary-800 hover:to-pepperoni text-accentwhite rounded-xl font-bold font-display shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 text-sm md:text-base"
                    >
                      <ShoppingCartIcon className="w-4 h-4 md:w-5 md:h-5" />
                      {getCurrentParticipant()?.meals?.length > 0 ? 'Update Meals' : 'Select Meals'}
                    </button>
                  )}

                  {/* Show message if user is in group but can't pay due to no meals */}
                  {getCurrentParticipant() &&
                    orderDetails?.status === 'open' &&
                    !canPay() &&
                    (!getCurrentParticipant()?.meals || getCurrentParticipant()?.meals?.length === 0) && (
                      <div className="w-full py-3 px-4 bg-yellow-50 border-2 border-yellow-200 text-yellow-800 rounded-xl font-bold font-display text-center text-sm md:text-base">
                        <ExclamationTriangleIcon className="w-4 h-4 md:w-5 md:h-5 inline mr-2" />
                        Add meals before payment
                      </div>
                    )}

                  {/* Secondary Actions */}
                  <div className="flex gap-3">
                    {getCurrentParticipant() && !isCreator() && orderDetails?.status === 'open' && (
                      <button
                        onClick={leaveGroupOrder}
                        className="flex-1 md:flex-none py-3 px-4 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold font-display shadow-lg hover:shadow-xl transition-all duration-300 text-sm md:text-base"
                      >
                        Leave
                      </button>
                    )}

                    {isCreator() && orderDetails?.status === 'open' && (
                      <button
                        onClick={cancelGroupOrder}
                        className="flex-1 md:flex-none py-3 px-4 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold font-display shadow-lg hover:shadow-xl transition-all duration-300 text-sm md:text-base"
                      >
                        Cancel
                      </button>
                    )}

                    <button
                      onClick={onClose}
                      className="flex-1 md:flex-none py-3 px-4 border-2 border-secondary-300 text-olive-light rounded-xl font-bold font-display hover:bg-secondary-50 transition-all duration-300 text-sm md:text-base"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Meal Selector Modal */}
          <GroupOrderMealSelector
            isOpen={showMealSelector}
            onClose={() => setShowMealSelector(false)}
            groupOrder={orderDetails}
            onMealsAdded={() => {
              fetchOrderDetails();
              fetchPaymentStatus();
              onUpdate();
            }}
            onPaymentComplete={() => {
              fetchOrderDetails();
              fetchPaymentStatus();
              onUpdate();
            }}
          />
        </div>
      )}
    </AnimatePresence>
  );
};

export default GroupOrderDetails;